import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, Clock, Users, Headphones, ArrowRight, Calendar, MapPin, DollarSign } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SectionHeader from '@/components/common/SectionHeader';

interface BookingConfirmationProps {
  bookingId?: string;
  serviceName?: string;
  serviceDate?: string;
  location?: string;
  price?: number;
  isDemo?: boolean;
}

export default function BookingConfirmation({ 
  bookingId = 'RW-2024-089456',
  serviceName = 'End of Tenancy Cleaning',
  serviceDate = 'March 28, 2024 at 10:00 AM',
  location = 'London, UK',
  price = 299,
  isDemo = true
}: BookingConfirmationProps) {
  const [timelineStep, setTimelineStep] = useState(1);
  const navigate = useNavigate();

  // Demo progression effect
  useEffect(() => {
    if (isDemo) {
      const interval = setInterval(() => {
        setTimelineStep((prev) => (prev < 4 ? prev + 1 : 1));
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isDemo]);

  const nextSteps = [
    {
      step: 1,
      title: 'We Review Your Request',
      description: 'Our team is reviewing your service details to confirm availability and prepare your service plan.',
      timeline: '2-4 hours',
      icon: CheckCircle2
    },
    {
      step: 2,
      title: 'Provider Assignment',
      description: 'We assign a vetted, professional provider matched to your specific needs.',
      timeline: '4-8 hours',
      icon: Users
    },
    {
      step: 3,
      title: 'Confirmation Call',
      description: 'Our team calls to confirm details, answer questions, and schedule your service.',
      timeline: '8-12 hours',
      icon: Headphones
    },
    {
      step: 4,
      title: 'Service Delivery',
      description: 'Your service provider arrives at the scheduled time. We monitor quality throughout.',
      timeline: 'On schedule',
      icon: CheckCircle2
    }
  ];

  const whyPlatform = [
    {
      icon: CheckCircle2,
      title: 'Vetted Professionals',
      description: 'Every provider is thoroughly vetted for quality and reliability'
    },
    {
      icon: Headphones,
      title: '24/7 Support',
      description: 'Our team is available around the clock for questions or issues'
    },
    {
      icon: Clock,
      title: 'Time-Saving',
      description: 'No more juggling multiple contractor calls and negotiations'
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Booking Confirmed | Virewire</title>
        <meta name="description" content="Your service request has been confirmed. We'll manage the rest." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {/* Success Hero */}
      <section className="bg-gradient-to-b from-primary to-primary/90 text-primary-foreground section-padding">
        <div className="container-custom text-center">
          {/* Success Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-accent/20 mb-6 animate-pulse">
            <CheckCircle2 className="w-12 h-12 text-accent" />
          </div>

          {/* Success Message */}
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Request Confirmed!
          </h1>
          <p className="text-xl text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
            We've received your service request and our team is already preparing your service plan. 
            You'll hear from us shortly.
          </p>

          {/* Booking ID */}
          <div className="bg-primary-foreground/10 rounded-lg inline-block px-6 py-3 backdrop-blur-sm">
            <p className="text-sm text-primary-foreground/70 mb-1">Booking Reference</p>
            <p className="text-xl font-mono font-bold text-primary-foreground">{bookingId}</p>
          </div>
        </div>
      </section>

      {/* Booking Details Card */}
      <section className="section-padding -mt-12 relative z-10">
        <div className="container-custom">
          <Card className="shadow-card-lg">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Left: Service Details */}
                <div>
                  <h3 className="text-lg font-bold text-foreground mb-6">Your Service Details</h3>
                  
                  <div className="space-y-4">
                    {/* Service Type */}
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-foreground/60 mb-1">Service</p>
                        <p className="font-semibold text-foreground">{serviceName}</p>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-foreground/60 mb-1">Scheduled Date & Time</p>
                        <p className="font-semibold text-foreground">{serviceDate}</p>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-foreground/60 mb-1">Location</p>
                        <p className="font-semibold text-foreground">{location}</p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <DollarSign className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-foreground/60 mb-1">Fixed Price</p>
                        <p className="font-semibold text-foreground">£{price}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: What Happens Next */}
                <div>
                  <h3 className="text-lg font-bold text-foreground mb-6">What Happens Next</h3>
                  
                  <div className="space-y-3">
                    <div className="p-4 bg-accent/5 border border-accent/20 rounded-lg">
                      <p className="text-sm font-semibold text-accent mb-1">Next 2-4 Hours</p>
                      <p className="text-sm text-foreground/70">
                        Our team reviews your request and prepares your personalized service plan.
                      </p>
                    </div>

                    <div className="p-4 bg-accent/5 border border-accent/20 rounded-lg">
                      <p className="text-sm font-semibold text-accent mb-1">Within 12 Hours</p>
                      <p className="text-sm text-foreground/70">
                        We assign a vetted provider and our team calls to confirm all details.
                      </p>
                    </div>

                    <div className="p-4 bg-accent/5 border border-accent/20 rounded-lg">
                      <p className="text-sm font-semibold text-accent mb-1">On Your Schedule</p>
                      <p className="text-sm text-foreground/70">
                        Your provider arrives on time. We monitor quality throughout the service.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Managed Service Timeline */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionHeader
            title="Your Managed Service Journey"
            subtitle="This is what managed service delivery looks like"
            centered={true}
          />

          <div className="grid md:grid-cols-4 gap-6 mt-12">
            {nextSteps.map((item) => {
              const IconComponent = item.icon;
              const isActive = timelineStep === item.step;
              const isCompleted = timelineStep > item.step;

              return (
                <div key={item.step} className="relative">
                  {/* Card */}
                  <Card className={`h-full transition-all ${
                    isActive 
                      ? 'border-accent shadow-card-lg' 
                      : isCompleted 
                      ? 'border-accent/50 bg-accent/5'
                      : 'border-border'
                  }`}>
                    <CardContent className="p-6 flex flex-col h-full">
                      {/* Step badge */}
                      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-bold mb-4 ${
                        isActive
                          ? 'bg-accent text-primary'
                          : isCompleted
                          ? 'bg-accent/10 text-accent'
                          : 'bg-canvas-muted text-foreground/50'
                      }`}>
                        {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : item.step}
                      </div>

                      {/* Icon */}
                      <IconComponent className={`w-6 h-6 mb-4 ${
                        isActive || isCompleted ? 'text-accent' : 'text-foreground/40'
                      }`} />

                      {/* Title */}
                      <h4 className={`font-bold mb-2 ${
                        isActive ? 'text-foreground' : 'text-foreground/70'
                      }`}>
                        {item.title}
                      </h4>

                      {/* Description */}
                      <p className="text-sm text-foreground/60 mb-4 flex-grow">
                        {item.description}
                      </p>

                      {/* Timeline */}
                      <p className={`text-xs font-semibold ${
                        isActive ? 'text-accent' : 'text-foreground/50'
                      }`}>
                        <Clock className="w-3 h-3 inline mr-1" />
                        {item.timeline}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Arrow connector */}
                  {item.step < nextSteps.length && (
                    <div className="hidden md:block absolute -right-6 top-1/2 transform -translate-y-1/2 w-12 h-0.5 bg-gradient-to-r from-border to-transparent" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Our Platform */}
      <section className="section-padding bg-gradient-to-b from-background to-primary/5">
        <div className="container-custom">
          <SectionHeader
            title="Why You Chose Our Managed Platform"
            subtitle="What sets us apart from hiring contractors directly"
            centered={true}
          />

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {whyPlatform.map((item, idx) => {
              const IconComponent = item.icon;
              return (
                <Card key={idx} className="border-border hover:shadow-card-lg transition-shadow">
                  <CardContent className="p-6">
                    <IconComponent className="w-8 h-8 text-accent mb-4" />
                    <h3 className="font-bold text-lg text-foreground mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground/70">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="section-padding bg-background">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">Need Help?</h2>
          <p className="text-lg text-foreground/70 mb-8 max-w-2xl mx-auto">
            Our support team is available 24/7 to answer questions or make changes to your booking.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="outline" size="lg" asChild>
              <a href="tel:+44201234567">
                Call Us: 0201 234 567
              </a>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/contact">
                Contact Support
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            More Services Needed?
          </h2>
          <p className="text-lg text-primary-foreground/90 mb-8">
            Explore our full range of managed property services
          </p>
          <Button variant="hero" size="lg" asChild className="group">
            <Link to="/services">
              Browse All Services
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
}
